import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Sprout, Plus, Trash2, Tag, Search, X, Megaphone, History } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card } from '../components/ui/card';
import { Switch } from '../components/ui/switch';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import Navigation from '../components/Navigation';
import { getAdminHeaders, canEdit } from '../lib/auth';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LancarSementesExtras = () => {
  const navigate = useNavigate();
  const editAllowed = canEdit();

  // Shared state
  const [children, setChildren] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);

  // Launch form state
  const [search, setSearch] = useState('');
  const [selectedIds, setSelectedIds] = useState([]);
  const [quantidade, setQuantidade] = useState(5);
  const [justificativa, setJustificativa] = useState('');
  const [campanhaId, setCampanhaId] = useState('none');
  const [submitting, setSubmitting] = useState(false);

  // Campaign form state
  const [newCampaignName, setNewCampaignName] = useState('');
  const [newCampaignDesc, setNewCampaignDesc] = useState('');
  const [creatingCampaign, setCreatingCampaign] = useState(false);

  useEffect(() => {
    fetchAll();
  }, []);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [c, camp, hist] = await Promise.all([
        axios.get(`${API}/children?limit=500`),
        axios.get(`${API}/extra-seeds/campaigns`, { headers: getAdminHeaders() }),
        axios.get(`${API}/extra-seeds?limit=200`, { headers: getAdminHeaders() }),
      ]);
      setChildren((c.data || []).sort((a, b) => (a.nome || '').localeCompare(b.nome || '')));
      setCampaigns(camp.data || []);
      setHistory(hist.data || []);
    } catch (e) {
      console.error(e);
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  const filteredChildren = children.filter((c) =>
    (c.nome || '').toLowerCase().includes(search.toLowerCase())
  );

  const toggleChild = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const toggleAllVisible = () => {
    const visibleIds = filteredChildren.map((c) => c.id);
    const allSelected = visibleIds.every((id) => selectedIds.includes(id));
    if (allSelected) {
      setSelectedIds((prev) => prev.filter((id) => !visibleIds.includes(id)));
    } else {
      setSelectedIds((prev) => Array.from(new Set([...prev, ...visibleIds])));
    }
  };

  const handleLaunch = async () => {
    if (!editAllowed) {
      toast.error('Você não tem permissão para lançar sementes');
      return;
    }
    if (selectedIds.length === 0) {
      toast.error('Selecione pelo menos uma criança');
      return;
    }
    const qty = parseInt(quantidade, 10);
    if (!qty || qty < 1) {
      toast.error('Quantidade deve ser pelo menos 1');
      return;
    }
    if ((justificativa || '').trim().length < 5) {
      toast.error('Justificativa precisa ter pelo menos 5 caracteres');
      return;
    }
    setSubmitting(true);
    try {
      const body = {
        child_ids: selectedIds,
        quantidade: qty,
        justificativa: justificativa.trim(),
      };
      if (campanhaId && campanhaId !== 'none') body.campanha_id = campanhaId;
      const res = await axios.post(`${API}/extra-seeds/launch`, body, {
        headers: getAdminHeaders(),
      });
      toast.success(
        `${qty} sementes lançadas para ${res.data.total_criancas} criança(s) 🌱`
      );
      setSelectedIds([]);
      setJustificativa('');
      setQuantidade(5);
      setCampanhaId('none');
      fetchAll();
    } catch (e) {
      const msg = e?.response?.data?.detail || 'Erro ao lançar sementes';
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteEntry = async (entryId) => {
    if (!window.confirm('Tem certeza que deseja remover este lançamento?')) return;
    try {
      await axios.delete(`${API}/extra-seeds/${entryId}`, { headers: getAdminHeaders() });
      toast.success('Lançamento removido');
      fetchAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erro ao remover');
    }
  };

  const handleCreateCampaign = async () => {
    if (!editAllowed) return;
    const nome = (newCampaignName || '').trim();
    if (!nome) {
      toast.error('Nome da campanha é obrigatório');
      return;
    }
    setCreatingCampaign(true);
    try {
      await axios.post(
        `${API}/extra-seeds/campaigns`,
        { nome, descricao: newCampaignDesc.trim() },
        { headers: getAdminHeaders() }
      );
      toast.success('Campanha criada');
      setNewCampaignName('');
      setNewCampaignDesc('');
      fetchAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erro ao criar campanha');
    } finally {
      setCreatingCampaign(false);
    }
  };

  const handleToggleCampaign = async (camp) => {
    try {
      await axios.put(
        `${API}/extra-seeds/campaigns/${camp.id}`,
        { ativa: !camp.ativa },
        { headers: getAdminHeaders() }
      );
      fetchAll();
    } catch (e) {
      toast.error('Erro ao atualizar campanha');
    }
  };

  const handleDeleteCampaign = async (camp) => {
    if (!window.confirm(`Excluir campanha "${camp.nome}"?\nLançamentos existentes serão preservados, mas a campanha não estará mais disponível para selecionar.`)) return;
    try {
      await axios.delete(`${API}/extra-seeds/campaigns/${camp.id}`, { headers: getAdminHeaders() });
      toast.success('Campanha removida');
      fetchAll();
    } catch (e) {
      toast.error('Erro ao excluir campanha');
    }
  };

  const activeCampaigns = campaigns.filter((c) => c.ativa);

  return (
    <div className="min-h-screen bg-[#F9F9F7] pb-24" data-testid="lancar-extras-page">
      <div className="bg-[#1A4D2E] text-white px-6 py-6">
        <div className="max-w-4xl mx-auto">
          <button
            data-testid="back-button"
            onClick={() => navigate(-1)}
            className="mb-4 hover:bg-white/10 rounded-full p-2 transition-all"
          >
            <ArrowLeft className="w-6 h-6" />
          </button>
          <div className="flex items-center gap-3">
            <Sprout className="w-7 h-7" strokeWidth={2.5} />
            <h1 className="font-nunito font-bold text-3xl">Sementes Extras</h1>
          </div>
          <p className="font-inter text-white/80 text-sm mt-2">
            Premie crianças por campanhas pontuais com justificativa
          </p>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-6">
        <Tabs defaultValue="lancar" className="w-full">
          <TabsList className="grid w-full grid-cols-3 mb-6">
            <TabsTrigger value="lancar" data-testid="tab-lancar">
              <Sprout className="w-4 h-4 mr-1" /> Lançar
            </TabsTrigger>
            <TabsTrigger value="historico" data-testid="tab-historico">
              <History className="w-4 h-4 mr-1" /> Histórico
            </TabsTrigger>
            <TabsTrigger value="campanhas" data-testid="tab-campanhas">
              <Megaphone className="w-4 h-4 mr-1" /> Campanhas
            </TabsTrigger>
          </TabsList>

          {/* TAB: LANÇAR */}
          <TabsContent value="lancar">
            <Card className="bg-white rounded-2xl p-6 shadow-md border border-[#E1ECE5] space-y-6">
              <div>
                <Label className="font-inter font-semibold text-[#1A4D2E]">Quantidade de sementes</Label>
                <Input
                  type="number"
                  min="1"
                  max="1000"
                  data-testid="input-quantidade"
                  value={quantidade}
                  onChange={(e) => setQuantidade(e.target.value)}
                  className="rounded-xl mt-2"
                />
                <p className="text-xs text-gray-500 mt-1">Sem limite semanal — ideal para campanhas pontuais</p>
              </div>

              <div>
                <Label className="font-inter font-semibold text-[#1A4D2E]">
                  Justificativa <span className="text-red-500">*</span>
                </Label>
                <Textarea
                  data-testid="input-justificativa"
                  value={justificativa}
                  onChange={(e) => setJustificativa(e.target.value)}
                  placeholder="Ex: Trouxe 3 visitantes na campanha de Páscoa"
                  maxLength={200}
                  className="rounded-xl mt-2 min-h-[80px]"
                />
                <p className="text-xs text-gray-500 mt-1">
                  {justificativa.length}/200 — mín 5 caracteres
                </p>
              </div>

              <div>
                <Label className="font-inter font-semibold text-[#1A4D2E]">
                  Campanha (opcional)
                </Label>
                <Select value={campanhaId} onValueChange={setCampanhaId}>
                  <SelectTrigger className="rounded-xl mt-2" data-testid="select-campanha">
                    <SelectValue placeholder="Sem campanha" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Sem campanha</SelectItem>
                    {activeCampaigns.map((c) => (
                      <SelectItem key={c.id} value={c.id}>
                        {c.nome}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {activeCampaigns.length === 0 && (
                  <p className="text-xs text-gray-500 mt-1">
                    Nenhuma campanha cadastrada. Vá na aba Campanhas para criar.
                  </p>
                )}
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <Label className="font-inter font-semibold text-[#1A4D2E]">
                    Crianças ({selectedIds.length} selecionada{selectedIds.length !== 1 ? 's' : ''})
                  </Label>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={toggleAllVisible}
                    data-testid="btn-toggle-all"
                  >
                    {filteredChildren.every((c) => selectedIds.includes(c.id))
                      ? 'Desmarcar todos'
                      : 'Selecionar todos'}
                  </Button>
                </div>
                <div className="relative mb-2">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    data-testid="input-search-children"
                    placeholder="Buscar por nome..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="rounded-xl pl-9"
                  />
                </div>
                <div className="border border-gray-200 rounded-xl max-h-72 overflow-y-auto bg-gray-50/40">
                  {filteredChildren.length === 0 ? (
                    <p className="p-4 text-sm text-gray-500 text-center">Nenhuma criança encontrada</p>
                  ) : (
                    filteredChildren.map((c) => {
                      const checked = selectedIds.includes(c.id);
                      return (
                        <button
                          key={c.id}
                          type="button"
                          onClick={() => toggleChild(c.id)}
                          data-testid={`child-row-${c.id}`}
                          className={`w-full flex items-center gap-3 px-4 py-3 border-b last:border-b-0 transition-colors ${
                            checked ? 'bg-[#1A4D2E]/10' : 'hover:bg-gray-100'
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={checked}
                            readOnly
                            className="w-4 h-4 accent-[#1A4D2E] pointer-events-none"
                          />
                          <span className="font-inter text-sm text-[#1A4D2E] flex-1 text-left">
                            {c.codigo ? `${c.codigo} · ` : ''}{c.nome}
                          </span>
                          <span className="text-xs text-gray-500">{c.idade ?? '-'}a</span>
                        </button>
                      );
                    })
                  )}
                </div>
              </div>

              <Button
                type="button"
                onClick={handleLaunch}
                disabled={submitting || !editAllowed}
                data-testid="btn-launch"
                className="w-full bg-[#1A4D2E] hover:bg-[#145A32] text-white rounded-full py-6 text-lg font-semibold shadow-md active:scale-95 transition-all flex items-center justify-center gap-2"
              >
                <Sprout className="w-5 h-5" />
                {submitting
                  ? 'Lançando...'
                  : `Lançar ${quantidade || 0} semente${(quantidade || 0) !== 1 ? 's' : ''} para ${selectedIds.length} criança${selectedIds.length !== 1 ? 's' : ''}`}
              </Button>
              {!editAllowed && (
                <p className="text-xs text-gray-500 text-center">
                  Você está em modo de visualização. Solicite permissão de edição para lançar sementes.
                </p>
              )}
            </Card>
          </TabsContent>

          {/* TAB: HISTÓRICO */}
          <TabsContent value="historico">
            <Card className="bg-white rounded-2xl p-4 md:p-6 shadow-md border border-[#E1ECE5]">
              <h2 className="font-nunito font-bold text-lg text-[#1A4D2E] mb-4">
                Últimos lançamentos ({history.length})
              </h2>
              {loading ? (
                <p className="text-center text-gray-500 py-8">Carregando...</p>
              ) : history.length === 0 ? (
                <p className="text-center text-gray-500 py-8">Nenhum lançamento ainda</p>
              ) : (
                <div className="space-y-3" data-testid="history-list">
                  {history.map((h) => (
                    <div
                      key={h.id}
                      data-testid={`history-item-${h.id}`}
                      className="flex items-start justify-between gap-3 bg-gray-50 rounded-xl p-3 border border-gray-100"
                    >
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-nunito font-bold text-[#1A4D2E]">
                            {h.child_name}
                          </span>
                          <span className="bg-[#F4D35E] text-[#1A4D2E] font-bold text-xs px-2 py-0.5 rounded-full">
                            +{h.quantidade} 🌱
                          </span>
                          {h.campanha_nome && (
                            <span className="bg-emerald-100 text-emerald-800 text-xs px-2 py-0.5 rounded-full inline-flex items-center gap-1">
                              <Tag className="w-3 h-3" /> {h.campanha_nome}
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-gray-700 mt-1 break-words">
                          {h.justificativa}
                        </p>
                        <p className="text-xs text-gray-500 mt-1">
                          {h.data} · por {h.created_by_name || h.created_by_email || '-'}
                        </p>
                      </div>
                      {editAllowed && (
                        <button
                          onClick={() => handleDeleteEntry(h.id)}
                          data-testid={`btn-delete-entry-${h.id}`}
                          className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition"
                          aria-label="Remover lançamento"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </Card>
          </TabsContent>

          {/* TAB: CAMPANHAS */}
          <TabsContent value="campanhas">
            <Card className="bg-white rounded-2xl p-6 shadow-md border border-[#E1ECE5] space-y-6">
              {editAllowed && (
                <div className="border-b border-gray-200 pb-6">
                  <h2 className="font-nunito font-bold text-lg text-[#1A4D2E] mb-3">
                    Nova campanha
                  </h2>
                  <div className="space-y-3">
                    <div>
                      <Label>Nome</Label>
                      <Input
                        data-testid="input-camp-nome"
                        value={newCampaignName}
                        onChange={(e) => setNewCampaignName(e.target.value)}
                        placeholder="Ex: Páscoa 2026"
                        className="rounded-xl mt-1"
                        maxLength={80}
                      />
                    </div>
                    <div>
                      <Label>Descrição (opcional)</Label>
                      <Textarea
                        data-testid="input-camp-desc"
                        value={newCampaignDesc}
                        onChange={(e) => setNewCampaignDesc(e.target.value)}
                        placeholder="Detalhes da campanha"
                        className="rounded-xl mt-1 min-h-[60px]"
                        maxLength={240}
                      />
                    </div>
                    <Button
                      type="button"
                      onClick={handleCreateCampaign}
                      disabled={creatingCampaign}
                      data-testid="btn-create-campaign"
                      className="bg-[#1A4D2E] hover:bg-[#145A32] text-white rounded-full px-6"
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      {creatingCampaign ? 'Criando...' : 'Criar campanha'}
                    </Button>
                  </div>
                </div>
              )}

              <div>
                <h2 className="font-nunito font-bold text-lg text-[#1A4D2E] mb-3">
                  Campanhas cadastradas ({campaigns.length})
                </h2>
                {campaigns.length === 0 ? (
                  <p className="text-center text-gray-500 py-6">Nenhuma campanha cadastrada</p>
                ) : (
                  <div className="space-y-2" data-testid="campaigns-list">
                    {campaigns.map((c) => (
                      <div
                        key={c.id}
                        data-testid={`campaign-row-${c.id}`}
                        className="flex items-center gap-3 bg-gray-50 rounded-xl p-3 border border-gray-100"
                      >
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="font-nunito font-bold text-[#1A4D2E]">
                              {c.nome}
                            </span>
                            {!c.ativa && (
                              <span className="bg-gray-200 text-gray-600 text-xs px-2 py-0.5 rounded-full">
                                inativa
                              </span>
                            )}
                          </div>
                          {c.descricao && (
                            <p className="text-sm text-gray-600 mt-1 break-words">
                              {c.descricao}
                            </p>
                          )}
                        </div>
                        {editAllowed && (
                          <>
                            <div className="flex items-center gap-2">
                              <Switch
                                data-testid={`switch-camp-${c.id}`}
                                checked={c.ativa}
                                onCheckedChange={() => handleToggleCampaign(c)}
                              />
                            </div>
                            <button
                              onClick={() => handleDeleteCampaign(c)}
                              data-testid={`btn-delete-camp-${c.id}`}
                              className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition"
                              aria-label="Excluir campanha"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      <Navigation />
    </div>
  );
};

export default LancarSementesExtras;
